import type { ShouldRevalidateFunction } from "react-router";

import { isAuthRevalidatingSubmit } from "./auth-revalidate";
import {
  isSpenderPageMounted,
  spenderShouldRevalidate,
} from "./fsastore-spender/spender-page";

/**
 * PLP pagination params that the grid manages client-side: `page`
 * (numeric) + `range` (the Start-From-Start fan-out marker). The grid
 * fetches against the loader using these in the URL, then calls
 * `navigate()` only to mutate the address bar — the loader must NOT
 * re-run as a result.
 *
 * `page=all` is intentionally NOT in this list: it flips the loader
 * into the fan-out branch and IS a real data change.
 */
export const CLIENT_PAGINATION_PARAMS = ["page", "range"] as const;

/**
 * `shouldRevalidate` for the catch-all dispatcher (`routes/$.tsx`).
 *
 * Decides whether RR7 should re-run the route loader on a client-side
 * navigation. We extract this into its own module so it can be unit-tested
 * without dragging in the dispatcher's server-only imports.
 *
 * Rules:
 *   - Explicit same-URL revalidations (`useRevalidator().revalidate()` — no
 *     form submit, identical href) are HONORED, mirroring root/layout's
 *     identical guard (jam-4, 2026-09-04): the revalidator family
 *     (TPA-consent write-back, LC guest-merge, perks enrollment) refreshed
 *     root/layout chrome while this route declined — the observed "header
 *     healed, hero stayed on the wrong arm" split. The three shouldRevalidate
 *     consumers now agree on this trigger.
 *   - Non-GET form submissions: skip unless it is an auth mutation
 *     (`isAuthRevalidatingSubmit` — login/register success, logout) or a
 *     submit with no `formAction`. Without the auth opt-in the catch-all's
 *     personalized Builder content (homepage etc.) kept rendering for the
 *     PREVIOUS identity after a SPA login/logout: root/layout revalidate via
 *     the same predicate, but this route sat on stale loader data until the
 *     next full page load.
 *   - Basket submissions (`/resources/basket` add / update / remove / coupon)
 *     do NOT revalidate (COM-830). Nothing this route's loader returns reads
 *     the basket — the minicart, flyout, Smart Spender notice, cart-spend and
 *     line-quantity readers all hang off `BasketProvider`, which refreshes
 *     from its own `/resources/basket` fetcher. The old carve-out (inherited
 *     from the boilerplate PDP route) re-ran the whole PDP / PLP / Builder
 *     loader on every add-to-cart (~0-6 SCAPI + CIO + Builder) and collapsed
 *     an accumulated PLP Load More grid back to a single page.
 *   - Different pathname: revalidate (different route content).
 *   - Same pathname, different search: revalidate UNLESS the only difference
 *     is in client-managed PLP pagination params (numeric `page` / `range`).
 *     `page=all` flips the loader into the fan-out branch and IS a real
 *     data change — must revalidate.
 *   - Same URL: no revalidation needed.
 *
 * Background — why the page-skip exists: PLP Load More + Start-From-Start
 * fetch via `useFetcher`, merge the response client-side, and then call
 * `navigate()` only to mutate the address bar. Revalidating would discard
 * the accumulated grid and replace it with a single page's worth of hits.
 */
export const dispatcherShouldRevalidate: ShouldRevalidateFunction = args => {
  const {
    formMethod,
    formAction,
    actionStatus,
    currentUrl,
    nextUrl,
    defaultShouldRevalidate,
  } = args;
  // COM-474: on the Builder spender page, chips and basket POSTs never re-run
  // the loader. The page records its own path when it mounts (COM-883).
  if (
    currentUrl.pathname === nextUrl.pathname &&
    isSpenderPageMounted(currentUrl.pathname)
  ) {
    return spenderShouldRevalidate(args);
  }
  // Explicit `useRevalidator().revalidate()` (no form submit + same URL):
  // honor it, same guard as root.tsx/layout.tsx. A revalidation exists to
  // refresh the CURRENT page — declining here left the catch-all's
  // personalized content stale while root/layout chrome healed (jam-4).
  if (
    !formMethod &&
    currentUrl.href === nextUrl.href &&
    defaultShouldRevalidate
  ) {
    return true;
  }
  if (formMethod !== "GET" && formMethod !== undefined) {
    if (isAuthRevalidatingSubmit({ formMethod, formAction, actionStatus })) {
      return true;
    }
    // COM-830: every other non-GET submit — basket mutations included —
    // keeps the current loader data (see the rules above).
    return !formAction;
  }
  if (currentUrl.pathname !== nextUrl.pathname) return true;
  if (currentUrl.search !== nextUrl.search) {
    if (
      stripClientPagination(currentUrl.search) ===
      stripClientPagination(nextUrl.search)
    ) {
      return false;
    }
    return true;
  }
  return false;
};

/**
 * Strip the client-managed pagination params (`page` numeric, `range`) from
 * a search string for canonical-comparison purposes. `page=all` is preserved
 * because it's a real data change. Exported for tests.
 */
export function stripClientPagination(search: string): string {
  const params = new URLSearchParams(search);
  for (const name of CLIENT_PAGINATION_PARAMS) {
    if (name === "page" && params.get("page") === "all") continue;
    params.delete(name);
  }
  return params.toString();
}
